import { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { motion, AnimatePresence } from 'motion/react';
import { User, Mail, Phone, Globe, Building2, Star, Camera, Lock, ChevronRight, Save, Plus, X, Search, Check, Trash2, AlertTriangle, FileCheck, PanelLeft, PanelRight } from 'lucide-react';
import { useAuthStore, useAuth } from '@/features/auth';
import { useUpdateProfile, useMyHospitals, useSetPrimaryHospital, useAddMyHospitals, useRemoveMyHospital } from '../hooks';
import { useHospitals } from '@/features/hospitals';
import { useUpload } from '@/features/messaging/hooks/useUpload';
import { UserAvatar } from '@/components/ui';
import { BaseModal, useModalClose } from '@/components/modal/BaseModal';
import useMediaQuery, { MD_BREAKPOINT } from '@/hooks/useMediaQuery';
import { http } from '@/network/http';
import toast from 'react-hot-toast';
import { ADMIN_COLORS } from '@/constants/theme';
import { useLayout, type SidebarSide } from '@/components/layout/LayoutContext';
import { switchLanguage } from '@/utils/langTransition';
import { validateEnglishName, validateHebrewName, validatePhone } from '@/utils/nameValidation';
import type { LucideIcon } from 'lucide-react';

function zodEnglishName() {
  return z.string().superRefine((val, ctx) => {
    const err = validateEnglishName(val);
    if (err) ctx.addIssue({ code: z.ZodIssueCode.custom, message: err });
  });
}
function zodHebrewName() {
  return z.string().superRefine((val, ctx) => {
    const err = validateHebrewName(val);
    if (err) ctx.addIssue({ code: z.ZodIssueCode.custom, message: err });
  });
}
function zodPhone() {
  return z.string().superRefine((val, ctx) => {
    const err = validatePhone(val);
    if (err) ctx.addIssue({ code: z.ZodIssueCode.custom, message: err });
  });
}

const profileSchema = z.object({
  firstName: zodEnglishName(),
  lastName: zodEnglishName(),
  firstNameHe: zodHebrewName(),
  lastNameHe: zodHebrewName(),
  phone: zodPhone(),
  preferredLanguage: z.enum(['he', 'en']),
});

// ── Local sub-components ──────────────────────────────────────

function SectionLabel({ text }: { text: string }) {
  return (
    <h3 className="px-1 text-sm font-bold tracking-wider text-neutral-700">
      {text}
    </h3>
  );
}

function InfoRow({
  icon: Icon,
  label,
  readOnly,
  dir,
  children,
}: {
  icon: LucideIcon;
  label: string;
  readOnly?: boolean;
  dir?: 'ltr' | 'rtl';
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-3 px-4 py-3" dir={dir}>
      <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-primary-50">
        <Icon className="h-4 w-4 text-primary-600" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[11px] font-medium uppercase tracking-wide text-neutral-400">{label}</p>
        {children}
      </div>
      {readOnly ? (
        <Lock className="h-4 w-4 flex-shrink-0 text-neutral-300" />
      ) : (
        <ChevronRight className="h-4 w-4 flex-shrink-0 text-neutral-300" />
      )}
    </div>
  );
}

function Divider() {
  return <div className="mx-4 border-t border-neutral-100" />;
}

/** Header for view-terms modal: drag handle + title + X button (uses animated close) */
function TermsModalHeader({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const isMobile = useMediaQuery(MD_BREAKPOINT);
  const animatedClose = useModalClose();
  const handleClose = animatedClose || onClose;
  const isRTL = typeof document !== 'undefined' ? document.documentElement.dir === 'rtl' : false;
  return (
    <div className="flex-shrink-0">
      {isMobile && (
        <div className="flex justify-center pt-2 pb-1">
          <div className="w-12 h-1.5 rounded-full" style={{ backgroundColor: 'rgba(0,0,0,0.15)' }} />
        </div>
      )}
      <div className="flex items-center justify-center relative px-5 py-3">
        <h2 className="text-lg font-bold text-neutral-800">{t('terms.title')}</h2>
        <button
          onClick={handleClose}
          className="absolute flex items-center justify-center w-8 h-8 rounded-full transition-all active:scale-95"
          style={{
            [isRTL ? 'left' : 'right']: 16,
            backgroundColor: 'rgba(255,255,255,0.85)',
            border: '1px solid rgba(148,163,184,0.2)',
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          }}
          aria-label={t('common.close')}
        >
          <X className="h-4 w-4 text-neutral-700" />
        </button>
      </div>
      <p className="px-5 pb-2 text-sm text-neutral-500">{t('terms.subtitle')}</p>
    </div>
  );
}

/** Header for delete-account modal: drag handle + title + X button (uses animated close) */
function DeleteAccountHeader({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const isMobile = useMediaQuery(MD_BREAKPOINT);
  const animatedClose = useModalClose();
  const handleClose = animatedClose || onClose;
  const isRTL = typeof document !== 'undefined' ? document.documentElement.dir === 'rtl' : false;
  return (
    <div className="flex-shrink-0">
      {isMobile && (
        <div className="flex justify-center pt-2 pb-1">
          <div className="w-12 h-1.5 rounded-full" style={{ backgroundColor: 'rgba(0,0,0,0.15)' }} />
        </div>
      )}
      <div className="flex items-center justify-center relative px-5 py-3">
        <div className="flex items-center gap-2 text-red-600">
          <AlertTriangle className="h-5 w-5" />
          <h2 className="text-lg font-bold">{t('settings.deleteAccount')}</h2>
        </div>
        <button
          onClick={handleClose}
          className="absolute flex items-center justify-center w-8 h-8 rounded-full transition-all active:scale-95"
          style={{
            [isRTL ? 'left' : 'right']: 16,
            backgroundColor: 'rgba(255,255,255,0.85)',
            border: '1px solid rgba(148,163,184,0.2)',
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          }}
          aria-label={t('common.close')}
        >
          <X className="h-4 w-4 text-neutral-700" />
        </button>
      </div>
      <p className="px-5 pb-2 text-sm text-neutral-600">{t('settings.deleteAccountConfirm')}</p>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────

export function ProfileSettingsPage() {
  const { t, i18n } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const updateProfile = useUpdateProfile();
  const { data: hospitals } = useMyHospitals();
  const setPrimaryHospital = useSetPrimaryHospital();
  const addMyHospitals = useAddMyHospitals();
  const removeMyHospital = useRemoveMyHospital();
  const { upload, isUploading } = useUpload();
  const { sidebarSide, setSidebarSide } = useLayout();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerSearch, setPickerSearch] = useState('');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteConfirmEmail, setDeleteConfirmEmail] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const { logout } = useAuth();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors, isDirty },
  } = useForm<z.infer<typeof profileSchema>>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      firstNameHe: user?.firstNameHe || '',
      lastNameHe: user?.lastNameHe || '',
      phone: user?.phone || '',
      preferredLanguage: (user?.preferredLanguage as 'he' | 'en') || 'he',
    },
  });

  const selectedLang = watch('preferredLanguage');

  useEffect(() => {
    if (user) {
      reset({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        firstNameHe: user.firstNameHe || '',
        lastNameHe: user.lastNameHe || '',
        phone: user.phone || '',
        preferredLanguage: (user.preferredLanguage as 'he' | 'en') || 'he',
      });
    }
  }, [user, reset]);

  const onSubmit = handleSubmit(async (data) => {
    try {
      await updateProfile.mutateAsync({
        firstName: data.firstName,
        lastName: data.lastName,
        firstNameHe: data.firstNameHe,
        lastNameHe: data.lastNameHe,
        phone: data.phone || null,
        preferredLanguage: data.preferredLanguage,
      });

      if (user) {
        setUser({
          ...user,
          firstName: data.firstName,
          lastName: data.lastName,
          firstNameHe: data.firstNameHe,
          lastNameHe: data.lastNameHe,
          phone: data.phone || null,
          preferredLanguage: data.preferredLanguage,
        });
      }

      if (data.preferredLanguage !== i18n.language) {
        await switchLanguage(i18n, data.preferredLanguage, false);
      }

      toast.success(t('users.profileUpdated'));
    } catch {
      toast.error(t('common.error'));
    }
  });

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const result = await upload(file, 'profile');
    if (result?.url) {
      try {
        await updateProfile.mutateAsync({ avatarUrl: result.url });
        if (user) {
          setUser({ ...user, avatarUrl: result.url });
        }
        toast.success(t('users.profileUpdated'));
      } catch {
        toast.error(t('common.error'));
      }
    }
    // Reset so the same file can be re-selected
    e.target.value = '';
  };

  const handleRemoveHospital = (hospitalId: string) => {
    removeMyHospital.mutate(hospitalId, {
      onSuccess: () => toast.success(t('users.hospitalRemoved')),
      onError: (err: any) => {
        const msg = err?.response?.data?.message;
        if (msg?.includes('primary')) {
          toast.error(t('users.cannotRemovePrimary'));
        } else {
          toast.error(t('common.error'));
        }
      },
    });
  };

  const handleDeleteAccount = async () => {
    try {
      setDeleting(true);
      await http.delete('/users/me');
      logout();
    } catch {
      toast.error(t('common.error'));
      setDeleting(false);
    }
  };

  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || '';
  const roleName =
    i18n.language === 'he'
      ? user?.employmentRole?.name
      : user?.employmentRole?.nameEn ?? user?.employmentRole?.name;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="mx-auto max-w-lg space-y-5 pb-28"
    >
      {/* ── Hero Card ── */}
      <div className="flex flex-col items-center rounded-2xl bg-white/70 px-6 py-8 shadow-sm backdrop-blur-md">
        {/* Avatar with camera overlay */}
        <div className="relative">
          <UserAvatar
            src={user?.avatarUrl}
            name={fullName}
            size="h-24 w-24"
            className="text-2xl ring-4 ring-white shadow-lg"
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            className="absolute -bottom-1 -end-1 flex h-8 w-8 items-center justify-center rounded-full bg-primary-600 text-white shadow-md transition-transform hover:scale-105 active:scale-95 disabled:opacity-50 cursor-pointer"
          >
            <Camera className="h-4 w-4" />
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleAvatarUpload}
          />
        </div>

        <h2 className="mt-4 text-xl font-bold text-neutral-800">{fullName}</h2>

        {roleName && (
          <span className="mt-1.5 inline-block rounded-full bg-primary-50 px-3 py-0.5 text-xs font-semibold text-primary-700">
            {roleName}
          </span>
        )}

        <p className="mt-1 text-sm text-neutral-400">{user?.email}</p>
      </div>

      {/* ── Personal Information ── */}
      <form onSubmit={onSubmit}>
        {/* Name in English */}
        <div dir="ltr"><SectionLabel text={t('auth.nameInEnglish')} /></div>
        <div className="mt-2 overflow-hidden rounded-2xl bg-white/70 shadow-sm backdrop-blur-md" dir="ltr">
          <InfoRow icon={User} label={t('auth.firstNameEn')} dir="ltr">
            <input
              {...register('firstName')}
              dir="ltr"
              className="w-full bg-transparent text-sm text-neutral-800 outline-none placeholder:text-neutral-300 focus:border-b focus:border-primary-400"
            />
            {errors.firstName && <p className="mt-0.5 text-[11px] text-red-500">{t(errors.firstName.message!)}</p>}
          </InfoRow>
          <Divider />
          <InfoRow icon={User} label={t('auth.lastNameEn')} dir="ltr">
            <input
              {...register('lastName')}
              dir="ltr"
              className="w-full bg-transparent text-sm text-neutral-800 outline-none placeholder:text-neutral-300 focus:border-b focus:border-primary-400"
            />
            {errors.lastName && <p className="mt-0.5 text-[11px] text-red-500">{t(errors.lastName.message!)}</p>}
          </InfoRow>
        </div>

        {/* Name in Hebrew */}
        <div className="mt-5">
          <SectionLabel text={t('auth.nameInHebrew')} />
          <div className="mt-2 overflow-hidden rounded-2xl bg-white/70 shadow-sm backdrop-blur-md">
            <InfoRow icon={User} label={t('auth.firstNameHe')}>
              <input
                {...register('firstNameHe')}
                dir="rtl"
                className="w-full bg-transparent text-sm text-neutral-800 outline-none placeholder:text-neutral-300 focus:border-b focus:border-primary-400"
              />
              {errors.firstNameHe && <p className="mt-0.5 text-[11px] text-red-500">{t(errors.firstNameHe.message!)}</p>}
            </InfoRow>
            <Divider />
            <InfoRow icon={User} label={t('auth.lastNameHe')}>
              <input
                {...register('lastNameHe')}
                dir="rtl"
                className="w-full bg-transparent text-sm text-neutral-800 outline-none placeholder:text-neutral-300 focus:border-b focus:border-primary-400"
              />
              {errors.lastNameHe && <p className="mt-0.5 text-[11px] text-red-500">{t(errors.lastNameHe.message!)}</p>}
            </InfoRow>
          </div>
        </div>

        {/* Contact */}
        <div className="mt-5">
          <SectionLabel text={t('users.personalInfo')} />
          <div className="mt-2 overflow-hidden rounded-2xl bg-white/70 shadow-sm backdrop-blur-md">
            <InfoRow icon={Mail} label={t('auth.email')} readOnly>
              <p className="text-sm text-neutral-500">{user?.email}</p>
            </InfoRow>
            <Divider />
            <InfoRow icon={Phone} label={t('users.phone')}>
              <input
                {...register('phone')}
                dir="ltr"
                className="w-full bg-transparent text-sm text-neutral-800 outline-none placeholder:text-neutral-300 focus:border-b focus:border-primary-400"
              />
              {errors.phone && <p className="mt-0.5 text-[11px] text-red-500">{t(errors.phone.message!)}</p>}
            </InfoRow>
          </div>
        </div>

        {/* ── Preferences ── */}
        <div className="mt-5">
          <SectionLabel text={t('users.language')} />
          <div className="mt-2 overflow-hidden rounded-2xl bg-white/70 shadow-sm backdrop-blur-md">
            <div className="flex items-center gap-3 px-4 py-3">
              <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-primary-50">
                <Globe className="h-4 w-4 text-primary-600" />
              </div>
              <p className="flex-1 text-sm font-medium text-neutral-700">{t('users.language')}</p>
              <div className="flex gap-1.5">
                {(['he', 'en'] as const).map((lang) => (
                  <button
                    key={lang}
                    type="button"
                    onClick={() => setValue('preferredLanguage', lang, { shouldDirty: true })}
                    className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-colors cursor-pointer ${
                      selectedLang === lang
                        ? 'bg-primary-600 text-white shadow-sm'
                        : 'bg-neutral-100 text-neutral-500 hover:bg-neutral-200'
                    }`}
                  >
                    {lang === 'he' ? 'עברית' : 'English'}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ── Sidebar Position (desktop only) ── */}
        <div className="mt-5 hidden md:block">
          <SectionLabel text={t('settings.sidebarPosition')} />
          <div className="mt-2 overflow-hidden rounded-2xl bg-white/70 shadow-sm backdrop-blur-md">
            <div className="flex items-center gap-3 px-4 py-3">
              <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-primary-50">
                <PanelLeft className="h-4 w-4 text-primary-600" />
              </div>
              <p className="flex-1 text-sm font-medium text-neutral-700">{t('settings.sidebarPosition')}</p>
              <div className="flex gap-1.5">
                {(['left', 'right'] as const).map((side) => (
                  <button
                    key={side}
                    type="button"
                    onClick={() => setSidebarSide(side)}
                    className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-semibold transition-colors cursor-pointer ${
                      sidebarSide === side
                        ? 'bg-primary-600 text-white shadow-sm'
                        : 'bg-neutral-100 text-neutral-500 hover:bg-neutral-200'
                    }`}
                  >
                    {side === 'left' ? <PanelLeft className="h-3.5 w-3.5" /> : <PanelRight className="h-3.5 w-3.5" />}
                    {t(side === 'left' ? 'settings.sidebarLeft' : 'settings.sidebarRight')}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ── Terms of Use ── */}
        {user?.termsAcceptedAt && (
          <div className="mt-5">
            <SectionLabel text={t('terms.title')} />
            <div className="mt-2 overflow-hidden rounded-2xl bg-white/70 shadow-sm backdrop-blur-md">
              <div className="flex items-center gap-3 px-4 py-4">
                <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-blue-100">
                  <FileCheck className="h-4 w-4 text-blue-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-neutral-800">{t('terms.title')}</p>
                  <p className="text-xs text-neutral-500">
                    {t('settings.termsAcceptedOn', { date: new Date(user.termsAcceptedAt).toLocaleDateString(i18n.language === 'he' ? 'he-IL' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' }) })}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setTermsModalOpen(true)}
                  className="rounded-lg bg-blue-500 px-3 py-1.5 text-xs font-bold text-white transition-all active:scale-95 cursor-pointer"
                >
                  {t('settings.viewTerms')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Floating Save Pill ── */}
        <AnimatePresence>
          {isDirty && (
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 40 }}
              transition={{ type: 'spring', stiffness: 400, damping: 30 }}
              className="fixed inset-x-0 bottom-24 z-50 flex justify-center pointer-events-none"
            >
              <button
                type="submit"
                disabled={updateProfile.isPending}
                className="pointer-events-auto flex items-center gap-2 rounded-full bg-primary-600 px-6 py-3 text-sm font-semibold text-white shadow-xl transition-transform hover:scale-105 active:scale-95 disabled:opacity-60 cursor-pointer"
              >
                <Save className="h-4 w-4" />
                {updateProfile.isPending ? '...' : t('common.save')}
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </form>

      {/* ── My Hospitals ── */}
      <div>
        <SectionLabel text={t('users.myHospitals')} />
        <div className="mt-2 overflow-hidden rounded-2xl bg-white/70 shadow-sm backdrop-blur-md">
          {hospitals && hospitals.length > 0 ? (
            hospitals.map((h, idx) => (
              <div key={h.id}>
                {idx > 0 && <Divider />}
                <div className="flex items-center gap-3 px-4 py-3">
                  <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-primary-50">
                    <Building2 className="h-4 w-4 text-primary-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-neutral-800">
                      {i18n.language === 'en' && h.nameEn ? h.nameEn : h.name}
                    </p>
                    <p className="text-xs text-neutral-400">
                      {h.code} &middot; {i18n.language === 'en' && h.cityEn ? h.cityEn : h.city}
                    </p>
                  </div>
                  {h.isPrimary ? (
                    <Star
                      className="h-5 w-5 flex-shrink-0"
                      fill={ADMIN_COLORS.warning}
                      stroke={ADMIN_COLORS.warning}
                    />
                  ) : (
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => {
                          setPrimaryHospital.mutate(h.id, {
                            onSuccess: () => toast.success(t('users.hospitalsUpdated')),
                            onError: () => toast.error(t('common.error')),
                          });
                        }}
                        disabled={setPrimaryHospital.isPending}
                        className="flex-shrink-0 p-1 transition-colors cursor-pointer"
                        title={t('users.setPrimary')}
                      >
                        <Star className="h-5 w-5" fill="none" stroke="#cbd5e1" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleRemoveHospital(h.id)}
                        disabled={removeMyHospital.isPending}
                        className="flex-shrink-0 rounded-full p-1 text-neutral-400 transition-colors hover:bg-red-50 hover:text-red-500 cursor-pointer"
                        title={t('users.removeHospital')}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))
          ) : (
            <p className="px-4 py-6 text-center text-sm text-neutral-400">
              {t('users.noHospitalsAssigned')}
            </p>
          )}

          {/* Add Hospital row */}
          <Divider />
          <button
            type="button"
            onClick={() => { setPickerOpen(true); setPickerSearch(''); }}
            className="flex w-full items-center gap-3 px-4 py-3 text-primary-600 transition-colors hover:bg-primary-50/50 cursor-pointer"
          >
            <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full border-2 border-dashed border-primary-300">
              <Plus className="h-4 w-4" />
            </div>
            <span className="text-sm font-medium">{t('users.addHospital')}</span>
          </button>
        </div>
      </div>

      {/* ── Danger Zone ── */}
      <div className="mt-8">
        <SectionLabel text={t('settings.dangerZone')} />
        <div className="mt-2 overflow-hidden rounded-2xl bg-red-50/70 shadow-sm backdrop-blur-md border border-red-100">
          <div className="flex items-center gap-3 px-4 py-4">
            <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-red-100">
              <Trash2 className="h-4 w-4 text-red-600" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-red-800">{t('settings.deleteAccount')}</p>
              <p className="text-xs text-red-600/70">{t('settings.deleteAccountWarning')}</p>
            </div>
            <button
              type="button"
              onClick={() => setDeleteModalOpen(true)}
              className="rounded-lg bg-red-500 px-3 py-1.5 text-xs font-bold text-white transition-all active:scale-95 cursor-pointer"
            >
              {t('settings.deleteAccount')}
            </button>
          </div>
        </div>
      </div>

      {/* Delete Account Confirmation Modal */}
      <BaseModal isOpen={deleteModalOpen} onClose={() => setDeleteModalOpen(false)} ariaLabel={t('settings.deleteAccount')}>
        <DeleteAccountHeader onClose={() => setDeleteModalOpen(false)} />
        <div className="px-5">
          <p className="text-xs text-neutral-500 mb-2">{t('settings.deleteAccountTypeEmail')}</p>
          <input
            type="email"
            value={deleteConfirmEmail}
            onChange={(e) => setDeleteConfirmEmail(e.target.value)}
            placeholder={user?.email}
            dir="ltr"
            className="w-full rounded-xl bg-neutral-100 px-4 py-3 text-sm text-neutral-800 outline-none focus:ring-2 focus:ring-red-200 placeholder:text-neutral-400"
          />
        </div>
        <div className="px-5 py-4">
          <button
            type="button"
            onClick={handleDeleteAccount}
            disabled={deleteConfirmEmail !== user?.email || deleting}
            className="w-full rounded-xl bg-red-500 py-3 text-sm font-bold text-white transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
          >
            {deleting ? '...' : t('settings.deleteAccountButton')}
          </button>
        </div>
      </BaseModal>

      {/* View Terms of Use Modal */}
      <BaseModal isOpen={termsModalOpen} onClose={() => setTermsModalOpen(false)} ariaLabel={t('terms.title')}>
        <TermsModalHeader onClose={() => setTermsModalOpen(false)} />
        <div className="mx-5 flex-1 rounded-xl bg-neutral-50 p-4 text-sm text-neutral-700 leading-relaxed overflow-y-auto" style={{ overscrollBehaviorY: 'contain', touchAction: 'pan-y' }} onPointerDownCapture={(e) => e.stopPropagation()}>
          <h3 className="font-bold mb-2">{t('terms.eulaTitle')}</h3>
          <p className="mb-3">{t('terms.eulaIntro')}</p>
          <h4 className="font-semibold mt-3 mb-1">{t('terms.conductTitle')}</h4>
          <ul className="list-disc ps-5 space-y-1">
            <li>{t('terms.conductRule1')}</li>
            <li>{t('terms.conductRule2')}</li>
            <li>{t('terms.conductRule3')}</li>
            <li>{t('terms.conductRule4')}</li>
          </ul>
          <h4 className="font-semibold mt-3 mb-1">{t('terms.moderationTitle')}</h4>
          <p>{t('terms.moderationText')}</p>
          <h4 className="font-semibold mt-3 mb-1">{t('terms.terminationTitle')}</h4>
          <p>{t('terms.terminationText')}</p>
        </div>
        <div className="px-5 py-4">
          <button
            type="button"
            onClick={() => setTermsModalOpen(false)}
            className="w-full rounded-xl py-3 text-sm font-bold text-white transition-all active:scale-[0.98] cursor-pointer"
            style={{ background: 'linear-gradient(135deg, #1a4fa0 0%, #3b8dd4 100%)' }}
          >
            {t('common.close')}
          </button>
        </div>
      </BaseModal>

      {/* ── Hospital Picker Modal ── */}
      <HospitalPickerModal
        isOpen={pickerOpen}
        onClose={() => setPickerOpen(false)}
        search={pickerSearch}
        onSearchChange={setPickerSearch}
        assignedIds={new Set(hospitals?.map((h) => h.id) ?? [])}
        onConfirm={(ids) => {
          addMyHospitals.mutate(ids, {
            onSuccess: () => {
              toast.success(t('users.hospitalAdded'));
              setPickerOpen(false);
            },
            onError: () => toast.error(t('common.error')),
          });
        }}
        isPending={addMyHospitals.isPending}
      />
    </motion.div>
  );
}

// ── Hospital Picker Modal (multi-select) ─────────────────────

function HospitalPickerModal({
  isOpen,
  onClose,
  search,
  onSearchChange,
  assignedIds,
  onConfirm,
  isPending,
}: {
  isOpen: boolean;
  onClose: () => void;
  search: string;
  onSearchChange: (v: string) => void;
  assignedIds: Set<string>;
  onConfirm: (ids: string[]) => void;
  isPending: boolean;
}) {
  const { t, i18n } = useTranslation();
  const { data: allHospitals } = useHospitals({ isActive: 'true' });
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Reset selection when modal opens
  useEffect(() => {
    if (isOpen) setSelected(new Set());
  }, [isOpen]);

  const hospitalsList = allHospitals?.hospitals ?? [];

  const filtered = useMemo(() => {
    if (!search.trim()) return hospitalsList;
    const q = search.toLowerCase();
    return hospitalsList.filter(
      (h: any) =>
        h.name?.toLowerCase().includes(q) ||
        h.nameEn?.toLowerCase().includes(q) ||
        h.code?.toLowerCase().includes(q) ||
        h.city?.toLowerCase().includes(q),
    );
  }, [hospitalsList, search]);

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      mobileVariant="bottomsheet"
      ariaLabel={t('users.addHospital')}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-5 pt-5 pb-3">
        <h2 className="text-lg font-bold text-neutral-800">{t('users.addHospital')}</h2>
        <button
          type="button"
          onClick={onClose}
          className="rounded-full p-1.5 text-neutral-400 hover:bg-neutral-100 cursor-pointer"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Search */}
      <div className="px-5 pb-3">
        <div className="flex items-center gap-2 rounded-xl bg-neutral-100 px-3 py-2">
          <Search className="h-4 w-4 text-neutral-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={t('users.searchHospitals')}
            className="w-full bg-transparent text-sm text-neutral-800 outline-none placeholder:text-neutral-400"
            autoFocus
          />
        </div>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto px-2 pb-20" style={{ maxHeight: '60dvh' }}>
        {filtered.length === 0 ? (
          <p className="py-8 text-center text-sm text-neutral-400">{t('hospitals.noHospitals')}</p>
        ) : (
          filtered.map((h: any) => {
            const isAssigned = assignedIds.has(h.id);
            const isSelected = selected.has(h.id);
            const displayName = i18n.language === 'en' && h.nameEn ? h.nameEn : h.name;

            return (
              <button
                key={h.id}
                type="button"
                disabled={isAssigned}
                onClick={() => toggleSelect(h.id)}
                className={`flex w-full items-center gap-3 rounded-xl px-3 py-3 transition-colors cursor-pointer ${
                  isAssigned
                    ? 'opacity-50'
                    : isSelected
                      ? 'bg-primary-50'
                      : 'hover:bg-primary-50/60 active:bg-primary-100/60'
                }`}
              >
                {/* Checkbox area */}
                <div className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-md border-2 transition-colors ${
                  isAssigned
                    ? 'border-neutral-300 bg-neutral-100'
                    : isSelected
                      ? 'border-primary-600 bg-primary-600'
                      : 'border-neutral-300'
                }`}>
                  {(isAssigned || isSelected) && (
                    <Check className="h-3.5 w-3.5 text-white" />
                  )}
                </div>
                <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-primary-50">
                  <Building2 className="h-4 w-4 text-primary-600" />
                </div>
                <div className="min-w-0 flex-1 text-start">
                  <p className="text-sm font-medium text-neutral-800">{displayName}</p>
                  <p className="text-xs text-neutral-400">
                    {h.code} &middot; {h.city}
                  </p>
                </div>
              </button>
            );
          })
        )}
      </div>

      {/* Floating confirm button */}
      <AnimatePresence>
        {selected.size > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ type: 'spring', stiffness: 400, damping: 30 }}
            className="absolute inset-x-0 bottom-0 flex justify-center px-5 pb-5 pt-3 bg-gradient-to-t from-white/90 to-transparent"
          >
            <button
              type="button"
              disabled={isPending}
              onClick={() => onConfirm([...selected])}
              className="flex items-center gap-2 rounded-full bg-primary-600 px-6 py-3 text-sm font-semibold text-white shadow-xl transition-transform hover:scale-105 active:scale-95 disabled:opacity-60 cursor-pointer"
            >
              <Plus className="h-4 w-4" />
              {t('users.addHospital')} ({selected.size})
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </BaseModal>
  );
}
